import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { http, formatINR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Star, ShieldCheck, MapPin, Languages, GraduationCap, Video, Phone, Building2, Calendar, CheckCircle2, ArrowLeft } from "lucide-react";

const MODE_ICON = { video: Video, phone: Phone, in_clinic: Building2 };

export default function PractitionerDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [book, setBook] = useState({
    slot_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
    slot_time: "10:00",
    mode: "video",
    concern: "",
    payment_method: "upi",
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    http.get(`/practitioners/${id}`).then((r) => setP(r.data));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user || user === false) {
      toast.error("Please sign in to book a consultation");
      nav(`/login?redirect=/practitioner/${id}`);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await http.post("/consultations/book", { ...book, practitioner_id: id });
      setConfirmed(data);
      toast.success("Consultation confirmed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (!p) return <div className="p-10 text-center">Loading…</div>;
  const commission = (p.consultation_fee * 20) / 100;
  const doctorGets = p.consultation_fee - commission;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid={`practitioner-detail-${id}`}>
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-sm text-[#1E3A2B] font-semibold mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to practitioners
      </button>

      <div className="grid gap-8 lg:grid-cols-[1fr_400px]">
        <div>
          <div className="grid gap-6 sm:grid-cols-[220px_1fr] items-start">
            <div className="rounded-2xl overflow-hidden border border-[#E2DDD5] aspect-square bg-[#E8F0EA]">
              {p.photo_url ? (
                <img src={p.photo_url} alt={p.full_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <GraduationCap className="w-12 h-12 text-[#1E3A2B] opacity-30" />
                </div>
              )}
            </div>
            <div>
              <span className="badge-sage inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> {p.qualification ? `Verified ${p.qualification}` : "Verified BAMS Practitioner"}</span>
              <h1 className="mt-3 font-serif text-4xl font-bold text-[#1A201C] tracking-tight leading-tight">
                {p.display_title} {p.full_name}
              </h1>
              <div className="mt-2 flex items-center gap-3 text-sm text-[#5A6B5D]">
                <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-[#D97706] text-[#D97706]" /> <span className="font-mono">{p.rating || "New"}</span> {p.reviews_count > 0 && <span>({p.reviews_count})</span>}</span>
                <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {p.clinic_city}</span>
                <span className="font-mono">{p.years_experience}+ yrs</span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {(p.specializations || []).map((s) => (
                  <span key={s} className="badge-sage" style={{ background: "#E8F0EA" }}>{s}</span>
                ))}
              </div>
              <div className="mt-3 text-sm text-[#5A6B5D] flex items-center gap-1">
                <Languages className="w-4 h-4" /> {(p.languages || []).join(" · ")}
              </div>
            </div>
          </div>

          <div className="mt-8 bg-white border border-[#E2DDD5] rounded-2xl p-6">
            <h2 className="font-serif text-xl font-semibold text-[#1E3A2B]">About</h2>
            <p className="mt-3 text-[15px] text-[#1A201C] leading-relaxed">{p.bio}</p>
          </div>

          <div className="mt-6 bg-[#E8F0EA] border border-[#1E3A2B]/20 rounded-2xl p-6">
            <div className="text-xs font-mono uppercase text-[#1E3A2B]">Credentials — publicly verifiable</div>
            <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
              <Row label="Qualification" value={p.qualification || "BAMS Physician"} />
              <Row label="Registration No." value={p.bams_reg_no} />
              <Row label="Council Registration" value={p.council_reg_no} />
              <Row label="Registered With" value={p.ncism_council} />
              <Row label="Years of Practice" value={`${p.years_experience} years`} />
            </div>
          </div>
        </div>

        {confirmed ? (
          <aside className="bg-white border border-[#1E3A2B] rounded-3xl p-6 h-fit sticky top-28" data-testid="booking-confirmed">
            <div className="w-12 h-12 rounded-full bg-[#E8F0EA] flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-[#1E3A2B]" />
            </div>
            <h3 className="mt-4 font-serif text-2xl font-bold text-[#1A201C]">Consultation booked</h3>
            <p className="text-sm text-[#5A6B5D] mt-1">A confirmation has been sent to {confirmed.user_email}.</p>
            <div className="mt-5 bg-[#FAF8F5] border border-[#E2DDD5] rounded-xl p-4 text-sm space-y-1.5">
              <Row label="With" value={confirmed.practitioner_name} />
              <Row label="Date" value={confirmed.slot_date} />
              <Row label="Time" value={confirmed.slot_time} />
              <Row label="Mode" value={confirmed.mode.replace(/_/g, " ")} />
              <Row label="Booking ID" value={confirmed.id.slice(0, 8).toUpperCase()} />
            </div>
            {confirmed.mode === "video" && (
              <a href={confirmed.meeting_link} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex w-full items-center justify-center gap-2 btn-forest text-sm" data-testid="join-meeting">
                <Video className="w-4 h-4" /> Join Video Room
              </a>
            )}
            <div className="mt-4 text-xs text-[#5A6B5D]">
              Amount paid: <span className="font-bold text-[#1E3A2B]">{formatINR(confirmed.fee)}</span>
            </div>
          </aside>
        ) : (
          <form onSubmit={submit} className="bg-white border border-[#E2DDD5] rounded-3xl p-6 h-fit sticky top-28" data-testid="book-consultation-form">
            <div className="text-xs font-mono uppercase text-[#D97706]">Consultation fee (set by practitioner)</div>
            <div className="mt-1 font-serif text-4xl font-bold text-[#1E3A2B]">{formatINR(p.consultation_fee)}</div>
            <div className="text-xs text-[#5A6B5D]">{p.consultation_duration_min}-minute private session</div>

            <div className="mt-5 space-y-3">
              <div>
                <label className="text-xs font-mono uppercase text-[#5A6B5D]">Preferred date</label>
                <div className="mt-1 flex items-center gap-2 border border-[#E2DDD5] rounded-lg px-3 bg-[#FAF8F5]">
                  <Calendar className="w-4 h-4 text-[#5A6B5D]" />
                  <input
                    type="date"
                    value={book.slot_date}
                    min={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setBook({ ...book, slot_date: e.target.value })}
                    className="flex-1 bg-transparent py-2.5 text-sm outline-none"
                    required
                    data-testid="book-date"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-mono uppercase text-[#5A6B5D]">Preferred time</label>
                <select
                  value={book.slot_time}
                  onChange={(e) => setBook({ ...book, slot_time: e.target.value })}
                  className="mt-1 w-full border border-[#E2DDD5] bg-white rounded-lg p-2.5 text-sm"
                  data-testid="book-time"
                >
                  {["09:00", "10:00", "11:00", "14:00", "16:00", "18:00", "19:30"].map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-mono uppercase text-[#5A6B5D]">Mode</label>
                <div className="mt-1 grid grid-cols-3 gap-1.5">
                  {["video", "phone", "in_clinic"].map((m) => {
                    const Icon = MODE_ICON[m];
                    return (
                      <button
                        type="button"
                        key={m}
                        onClick={() => setBook({ ...book, mode: m })}
                        data-testid={`book-mode-${m}`}
                        className={`p-2 rounded-lg border text-[11px] font-semibold flex flex-col items-center gap-1 ${book.mode === m ? "bg-[#1E3A2B] text-white border-[#1E3A2B]" : "bg-white border-[#E2DDD5]"}`}
                      >
                        <Icon className="w-3.5 h-3.5" /> {m.replace(/_/g, " ")}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="text-xs font-mono uppercase text-[#5A6B5D]">Primary concern</label>
                <textarea
                  value={book.concern}
                  onChange={(e) => setBook({ ...book, concern: e.target.value })}
                  required
                  className="mt-1 w-full border border-[#E2DDD5] rounded-lg p-2.5 text-sm min-h-[70px]"
                  placeholder="Brief description helps the doctor prepare..."
                  data-testid="book-concern"
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase text-[#5A6B5D]">Payment</label>
                <div className="mt-1 grid grid-cols-3 gap-1.5">
                  {["upi", "card", "netbanking"].map((pay) => (
                    <button
                      type="button"
                      key={pay}
                      onClick={() => setBook({ ...book, payment_method: pay })}
                      data-testid={`book-pay-${pay}`}
                      className={`p-2 rounded-lg border text-[11px] font-semibold uppercase ${book.payment_method === pay ? "bg-[#1E3A2B] text-white border-[#1E3A2B]" : "bg-white border-[#E2DDD5]"}`}
                    >{pay}</button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-5 bg-[#FAF8F5] border border-[#E2DDD5] rounded-xl p-3 text-[11px] font-mono text-[#5A6B5D] space-y-0.5">
              <div className="flex justify-between"><span>Practitioner receives</span><span>{formatINR(doctorGets)}</span></div>
              <div className="flex justify-between"><span>Amiroop Healthion (20%)</span><span>{formatINR(commission)}</span></div>
              <div className="flex justify-between font-bold text-[#1E3A2B] pt-1 border-t border-[#E2DDD5]"><span>You pay</span><span>{formatINR(p.consultation_fee)}</span></div>
            </div>

            <button disabled={submitting} className="mt-4 w-full btn-forest inline-flex items-center justify-center gap-2" data-testid="book-submit">
              {submitting ? "Confirming…" : `Pay & Book Consultation`}
            </button>
            <p className="mt-2 text-[10px] font-mono text-center text-[#5A6B5D]">Mock payment · Cancel free up to 6 hrs before slot</p>
          </form>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[10px] font-mono uppercase text-[#5A6B5D]">{label}</span>
      <span className="text-sm font-semibold text-[#1A201C] text-right">{value}</span>
    </div>
  );
}
